import React, {
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
  useCallback,
} from "react";
import Config from "../../../Config";

// Ensure this file is treated as a module
export {};

// Declare global grecaptcha
declare global {
  interface Window {
    grecaptcha: any;
  }
}

interface ReCaptchaProps {
  onVerify: (token: string) => void;
  onExpired?: () => void;
  onError?: () => void;
  siteKey?: string;
}

export interface ReCaptchaRef {
  reset: () => void;
}

const ReCaptcha = forwardRef<ReCaptchaRef, ReCaptchaProps>(
  (
    {
      onVerify,
      onExpired,
      onError,
      siteKey = Config.captchaKey, // Use captcha key from config
    },
    ref,
  ) => {
    const recaptchaRef = useRef<HTMLDivElement>(null);
    const widgetId = useRef<number | null>(null);
    const isRendered = useRef<boolean>(false);

    // Store callbacks in refs to avoid re-renders
    const onVerifyRef = useRef(onVerify);
    const onExpiredRef = useRef(onExpired);
    const onErrorRef = useRef(onError);

    // Update refs when callbacks change
    useEffect(() => {
      onVerifyRef.current = onVerify;
    }, [onVerify]);

    useEffect(() => {
      onExpiredRef.current = onExpired;
    }, [onExpired]);

    useEffect(() => {
      onErrorRef.current = onError;
    }, [onError]);

    const reset = useCallback(() => {
      if (
        widgetId.current !== null &&
        window.grecaptcha &&
        window.grecaptcha.reset
      ) {
        try {
          window.grecaptcha.reset(widgetId.current);
        } catch (error) {
          console.error("Error resetting reCAPTCHA:", error);
        }
      }
    }, []);

    useImperativeHandle(ref, () => ({
      reset,
    }));

    useEffect(() => {
      // Don't render reCAPTCHA if no site key is configured
      if (!siteKey) {
        console.warn(
          "reCAPTCHA site key not configured. Please set REACT_APP_CAPTCHA_KEY environment variable.",
        );
        if (onErrorRef.current) {
          onErrorRef.current();
        }
        return;
      }

      // Don't re-render if already rendered
      if (isRendered.current) {
        return;
      }

      const loadRecaptcha = () => {
        if (
          window.grecaptcha &&
          window.grecaptcha.render &&
          recaptchaRef.current &&
          !isRendered.current
        ) {
          try {
            widgetId.current = window.grecaptcha.render(recaptchaRef.current, {
              sitekey: siteKey,
              callback: (token: string) => {
                if (onVerifyRef.current) {
                  onVerifyRef.current(token);
                }
              },
              "expired-callback": () => {
                if (onExpiredRef.current) {
                  onExpiredRef.current();
                }
              },
              "error-callback": () => {
                if (onErrorRef.current) {
                  onErrorRef.current();
                }
              },
              theme: "light",
              size: "normal",
            });
            isRendered.current = true;

            // Force z-index on reCAPTCHA elements after render
            setTimeout(() => {
              const recaptchaFrames = document.querySelectorAll(
                'iframe[src*="recaptcha"]',
              );
              recaptchaFrames.forEach((frame: any) => {
                if (frame.style) {
                  frame.style.zIndex = "9999999999999999";
                }
              });

              // Also set z-index on any reCAPTCHA divs
              const recaptchaDivs = document.querySelectorAll(
                'div[style*="z-index: 2000000000"], div[style*="z-index: 2000000001"]',
              );
              recaptchaDivs.forEach((div: any) => {
                if (div.style) {
                  div.style.zIndex = "9999999999999999";
                }
              });
            }, 100);

            // Watch for dynamically created reCAPTCHA elements (challenge popup)
            const observer = new MutationObserver((mutations) => {
              mutations.forEach((mutation) => {
                mutation.addedNodes.forEach((node: any) => {
                  if (node.nodeType === 1) {
                    // Element node
                    // Check for reCAPTCHA iframes
                    const frames = node.querySelectorAll
                      ? node.querySelectorAll('iframe[src*="recaptcha"]')
                      : [];
                    frames.forEach((frame: any) => {
                      if (frame.style) {
                        frame.style.zIndex = "9999999999999999";
                      }
                    });

                    // Check for reCAPTCHA divs with high z-index
                    const divs = node.querySelectorAll
                      ? node.querySelectorAll('div[style*="z-index"]')
                      : [];
                    divs.forEach((div: any) => {
                      if (
                        div.style &&
                        (div.style.zIndex === "2000000000" ||
                          div.style.zIndex === "2000000001")
                      ) {
                        div.style.zIndex = "9999999999999999";
                      }
                    });

                    // Check if the node itself is a reCAPTCHA element
                    if (
                      node.tagName === "IFRAME" &&
                      node.src &&
                      node.src.includes("recaptcha")
                    ) {
                      node.style.zIndex = "9999999999999999";
                    }
                    if (
                      node.tagName === "DIV" &&
                      node.style &&
                      (node.style.zIndex === "2000000000" ||
                        node.style.zIndex === "2000000001")
                    ) {
                      node.style.zIndex = "9999999999999999";
                    }
                  }
                });
              });
            });

            observer.observe(document.body, {
              childList: true,
              subtree: true,
            });

            // Store observer reference for cleanup
            (recaptchaRef.current as any)._observer = observer;
          } catch (error) {
            console.error("Error rendering reCAPTCHA:", error);
          }
        }
      };

      // Check if grecaptcha is already loaded
      if (window.grecaptcha && window.grecaptcha.render) {
        loadRecaptcha();
      } else {
        // Wait for grecaptcha to load
        const checkGrecaptcha = setInterval(() => {
          if (window.grecaptcha && window.grecaptcha.render) {
            clearInterval(checkGrecaptcha);
            loadRecaptcha();
          }
        }, 100);

        // Cleanup interval after 10 seconds
        setTimeout(() => {
          clearInterval(checkGrecaptcha);
        }, 10000);
      }

      return () => {
        // Cleanup observer
        if (recaptchaRef.current && (recaptchaRef.current as any)._observer) {
          (recaptchaRef.current as any)._observer.disconnect();
        }

        // Cleanup widget when component unmounts
        if (
          widgetId.current !== null &&
          window.grecaptcha &&
          window.grecaptcha.reset
        ) {
          try {
            window.grecaptcha.reset(widgetId.current);
          } catch (error) {
            console.error("Error resetting reCAPTCHA:", error);
          }
        }
        isRendered.current = false;
      };
    }, [siteKey]); // Only depend on siteKey, not the callback functions

    // Show message if no site key is configured
    if (!siteKey) {
      return (
        <div
          style={{
            padding: "10px",
            border: "1px solid #ccc",
            borderRadius: "4px",
            backgroundColor: "#f9f9f9",
            color: "#666",
            fontSize: "0.9em",
            textAlign: "center",
          }}
        >
          reCAPTCHA not configured
        </div>
      );
    }

    return (
      <div
        ref={recaptchaRef}
        style={{
          position: "relative",
          zIndex: 999999999999999, // Higher than modal z-index
        }}
      />
    );
  },
);

ReCaptcha.displayName = "ReCaptcha";

export default ReCaptcha;
